import { _ } from "libs";
/// <reference types="googlemaps" />

import React, { useState, useEffect, useContext, useRef, useLayoutEffect } from "react";
import ErrorBoundary from "../components/errorBoundary";
import CourtForm from "../components/courtFinder/courtForm";
import Map from "../components/map";
import UserContext from "../contexts/userContext";
import { searchSlots, getTimezoneByLocation } from "../actions";
import CourtResults from "../components/courtResults";
import CourtFilter, { CourtsFilter, defaultFilter } from "../components/courtFinder/courtFilter";
import { SearchResult, LocationSearchResult, Profile, AvailableSlot } from "../types";
import FavoriteClubs from "../components/favoriteClubs";
import Loader from "../components/loader";
import RecentClubs from "../components/recentClubs";
import { persistFilter, hydrateFilter } from "../libs/filterStorage";
import { IPLocationState } from "../reducers/ipLocation";
import { getLocalMomentInZone } from "../libs/time";
import { Subject } from "rxjs";
import { distinctUntilChanged, debounceTime } from "rxjs/operators";
import geocode from "../libs/geocode";
import { RouteChildrenProps } from "react-router";

const maxRange = 20;
const DEFAULT_RANGE = 10;

type ClosestSlots = {
	closestBeforeSlot: AvailableSlot;
	closestAfterSlot: AvailableSlot;
};

function findClosestSlots(slots: SearchResult, timeStamp: number): ClosestSlots {
	var closestBeforeTime: number = -86400,
		closestAfterTime: number = 86400,
		closestBeforeSlot: AvailableSlot,
		closestAfterSlot: AvailableSlot;

	for (let slot of slots.availableSlots) {
		let dif = slot.startDateTime - timeStamp;
		if (dif < 0 && dif > closestBeforeTime) {
			closestBeforeTime = dif;
			closestBeforeSlot = slot;
		}
		if (dif > 0 && dif < closestAfterTime) {
			closestAfterTime = dif;
			closestAfterSlot = slot;
		}
	}

	return {
		closestBeforeSlot,
		closestAfterSlot,
	};
}

function augmentResult(result: SearchResult, timestamp: number): SearchResult {
	result.searchTimeStamp = timestamp;

	for (let slot of result.availableSlots) {
		if (slot.startDateTime === timestamp) {
			result.className = "now";
			result.nearestSlot = slot;
			return result;
		}
	}

	let { closestBeforeSlot, closestAfterSlot } = findClosestSlots(result, timestamp);

	if (closestAfterSlot && closestBeforeSlot) {
		const absNext = Math.abs(closestAfterSlot.startDateTime - timestamp);
		const absPrev = Math.abs(closestBeforeSlot.startDateTime - timestamp);
		if (absNext > absPrev) {
			closestBeforeSlot = undefined;
		} else if (absNext === absPrev) {
			closestBeforeSlot = undefined;
		} else {
			closestAfterSlot = undefined;
		}
	}

	if (!closestAfterSlot && closestBeforeSlot) {
		result.nearestSlot = closestBeforeSlot;
		result.className = "earlier";
	} else if (!closestBeforeSlot && closestAfterSlot) {
		result.nearestSlot = closestAfterSlot;
		result.className = "later";
	} else {
		result.nearestSlot = null;
		result.className = "noSlot";
	}

	return result;
}

function augmentResults(res: SearchResult[], timestamp: number) {
	return res.map(result => {
		return augmentResult(result, timestamp);
	});
}

type CourtsState = {
	timestamp: number;
	latitude: number;
	longitude: number;
	city: string;
	timezone: string;
};

type AutoSaveProps = {
	location?: LocationSearchResult;
	timestamp?: number;
	timezone?: string;
};

function createEmptyState({ profile, location }: { profile: Profile; location: IPLocationState }): CourtsState {
	let timezone = profile.timeZone || location.time_zone;
	const { locationValid } = profile;

	return {
		timestamp: getLocalMomentInZone(profile.timeZone)
			.add(1, "h")
			.unix(),
		latitude: locationValid ? parseFloat(profile.latitude) : location.latitude,
		longitude: locationValid ? parseFloat(profile.longitude) : location.longitude,
		city: locationValid ? profile.city : location.city,
		timezone,
	};
}

const FILTER_NAME = "courtsFilter";

interface SearchStreamArgs {
	values: object;
	filter: CourtsFilter;
}

const Courts = ({ location, history }: RouteChildrenProps<any>) => {
	const userContext = useContext(UserContext);
	const state = useRef((location.state as CourtsState) || (createEmptyState(userContext) as CourtsState));

	const showResults = state !== undefined && location.pathname !== "/courts";

	const unmounted = useRef(false);
	let mapRef = useRef<Map>();

	const [subject$, setSubject] = useState<Subject<SearchStreamArgs>>(null);

	const [filter, setFilter] = useState(hydrateFilter<CourtsFilter>(FILTER_NAME, defaultFilter, userContext.userId));
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);

	useLayoutEffect(() => {
		const subject$ = new Subject<any>();
		const subscription = subject$
			.pipe(distinctUntilChanged(), debounceTime(300))
			.subscribe(({ values, filter }) => {
				const params = { ...state.current, ...values };
				state.current = params;
				fetchResults(params, getRange(), filter);
			});

		setSubject(subject$);

		return () => {
			subscription.unsubscribe();
			unmounted.current = true;
		};
	}, []);

	useEffect(() => {
		if (showResults && subject$ && !unmounted.current && mapRef.current !== null) {
			subject$.next({ values: {}, filter });
		}
	}, [showResults, subject$]);

	const getRange = () => {
		const map = mapRef.current;
		let range: number;
		if (map) {
			range = Math.min(maxRange, map.getRange());
		}

		return range || DEFAULT_RANGE;
	};

	const fetchResults = (
		{ latitude, longitude, timestamp: day }: { latitude: number; longitude: number; timestamp: number },
		range: number,
		filters
	) => {
		setLoading(true);

		searchSlots({
			...filters,
			latitude,
			longitude,
			range,
			day,
		}).then(res => {
			if (res) {
				setResults(augmentResults(res, day));
				setLoading(false);
			}
		});
	};

	const onFilterChange = async (filter: CourtsFilter) => {
		setFilter(filter);
		persistFilter(FILTER_NAME, filter, userContext.userId);
		subject$.next({ values: {}, filter });
	};

	const onMapMove = async (map: Map) => {
		const center = map.getCenter();

		try {
			const results = await geocode({ location: { lat: center.lat(), lng: center.lng() } });
			if (results.length > 0) {
				const timezone = await getTimezoneByLocation(center.lat(), center.lng());
				subject$.next({
					values: {
						latitude: center.lat(),
						longitude: center.lng(),
						city: results[0].formatted_address,
						timestamp: state.current.timestamp,
						timezone,
					},
					filter,
				});
			} else {
				setResults([]);
			}
		} catch (err) {
			//ignore
			setResults([]);
		} finally {
			setLoading(false);
		}
	};

	const onAutoSave = async ({ location, timestamp, timezone }: AutoSaveProps, diff: AutoSaveProps) => {
		if (location.type === "club") {
			history.push(`/club/${location.club.clubId}`, {
				timestamp,
			});
			return;
		}

		if (location.type === "place") {
			state.current.timezone = timezone;
			const placeChanged = location.value !== state.current.city;
			if (placeChanged) {
				let center: google.maps.LatLng;
				if (location.location) {
					center = new google.maps.LatLng(location.location.lat, location.location.lng);
				} else {
					center = await geocode({ address: location.value })[0].geometry.location;
				}

				mapRef.current.moveCenter(center);
				subject$.next({
					values: {
						latitude: center.lat(),
						longitude: center.lng(),
						city: location.value,
						timestamp,
					},
					filter,
				});
			} else {
				subject$.next({ values: { timestamp }, filter });
			}
		}
	};

	const { latitude, longitude } = state.current;
	const center = { lat: latitude, lng: longitude };

	return (
		<div className="courtsPage">
			<ErrorBoundary>
				<div className="contentLeft">
					<div className="courtsHeader">
						<h5>{_("courtForm.heading")}</h5>
						<CourtForm
							history={history}
							showSubmitButton={!showResults}
							timestamp={state.current.timestamp}
							city={state.current.city}
							onAutoSave={onAutoSave}
							timezone={state.current.timezone}
						/>
						{showResults ? (
							<CourtFilter
								onFilterChange={onFilterChange}
								value={hydrateFilter<CourtsFilter>(FILTER_NAME, defaultFilter, userContext.userId)}
							/>
						) : null}
					</div>
					{showResults ? (
						<div className="courtsBody">
							{loading ? (
								<Loader />
							) : (
								<CourtResults results={results} timestamp={state.current.timestamp} />
							)}
						</div>
					) : (
						<div className="courtsBody">
							<FavoriteClubs />
							<RecentClubs />
						</div>
					)}
				</div>
			</ErrorBoundary>
			<ErrorBoundary>
				<div className="contentRight">
					<div className="mapWrapper">
						<Map
							// @ts-ignore
							ref={mapRef}
							center={center}
							results={results}
							timestamp={state.current.timestamp}
							onMapMove={onMapMove}
						/>
					</div>
				</div>
			</ErrorBoundary>
		</div>
	);
};

export default Courts;
